/**
 * EditAndVerifyDrawer — reusable right-side slide-over drawer for editing and
 * verifying a single financial-profile field.
 *
 * Design spec (DESIGN-desktop-3.png / DESIGN-tier2-panel.png):
 *   - White card surface (bg-popover) on a scrim overlay.
 *   - Header: "EDIT & VERIFY" eyebrow (uppercase, muted) + field name (large bold)
 *     + circular X close button top-right.
 *   - Divider below header.
 *   - Two buttons: "Verify with bank" (primary) + "Upload document" (outline).
 *   - Helper line explaining the one-click journey.
 *   - Grey inset summary (TOTAL <FIELD> eyebrow + aggregate value + N sources).
 *   - "SOURCES" section header + "+ Add source" pill (right).
 *   - Source rows (white, bordered): display_name + value + verification badge + trash.
 *   - Sticky footer: full-width "Done" primary button.
 *
 * Wiring contract:
 *   - All edit state lives in the existing AggregateInputRowEdit / manualEntries
 *     passed by the parent. This drawer is a PRESENTATION shell only — it does
 *     not own edit logic.
 *   - onConnectBank / onUploadDocs wire to the same Plaid / document handlers.
 *   - onClose fires when the user presses X, Done, or clicks the scrim.
 *
 * Reuse: no passport-specific assumptions. Props are generic enough for
 * marketplace use.
 */

import {
  Banknote,
  BriefcaseBusiness,
  Landmark,
  Loader2,
  Plus,
  ShieldCheck,
  Trash2,
  Upload,
  UserRoundCheck,
  Wallet,
  X,
} from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useRef, useState, type ReactNode } from "react";

import { useMobileDrawerSwipe } from "../../../hooks/useMobileDrawerSwipe";

import type {
  ManualEntryKind,
  ManualEntryRequest,
  ManualFinancialEntry,
} from "../../../lib/onboarding-api";
import { formatCurrencyFromCents } from "../../../lib/financials";
import { cn } from "../../../lib/utils";
import { Button } from "../../ui/button";
import { TierBadge } from "../../ui/tier-badge";
import { ManualEntryForm } from "./rows/manual-entry-form";
import type { ProfileRowTier } from "./rows/tier-dot";
import type { ProfileRowId } from "../../../lib/readiness/factor-routing";

// ─── Types ───────────────────────────────────────────────────────────────────

/** Per-source verification tier for display in the source row badge. */
function entryTierFromSource(entry: ManualFinancialEntry): ProfileRowTier {
  switch (entry.verification_tier) {
    case "document":
      return "document-backed";
    case "connected":
      return "bank-verified";
    case "pre_approval":
      return "lender-approved";
    default:
      break;
  }
  const fam = entry.source_family ?? entry.source;
  if (fam === "document") return "document-backed";
  if (fam === "plaid") return "bank-verified";
  return "self-reported";
}

/** Format a single manual entry's value for the source row. */
function formatEntryValue(entry: ManualFinancialEntry): string {
  if (entry.entry_kind === "liability" && entry.monthly_payment_cents != null) {
    return `${formatCurrencyFromCents(entry.monthly_payment_cents)}/mo`;
  }
  if (entry.entry_kind === "income" && entry.income_amount_cents != null) {
    const freq = entry.income_frequency === "annual" ? "/yr" : "/mo";
    return `${formatCurrencyFromCents(entry.income_amount_cents)}${freq}`;
  }
  if (entry.entry_kind === "asset" && entry.asset_value_cents != null) {
    return formatCurrencyFromCents(entry.asset_value_cents);
  }
  return "—";
}

// ─── Props ───────────────────────────────────────────────────────────────────

export interface EditAndVerifyDrawerProps {
  /** Whether the drawer is visible. */
  open: boolean;
  /** Fires when the user closes (X, Done, scrim click). */
  onClose: () => void;

  // ── Field identity ─────────────────────────────────────────────────────────
  /** Display name for the field, e.g. "Income". */
  fieldLabel: string;
  /** Row ID used to pick the icon glyph. */
  rowId: ProfileRowId;

  // ── Value editor ───────────────────────────────────────────────────────────
  /**
   * Optional scalar editor rendered at the top of the body (e.g. the credit
   * band select or work-history select). When present it owns its own edit
   * state + mutation; the drawer is otherwise a presentation shell.
   */
  valueEditor?: ReactNode;
  /**
   * Whether to render the "Sources" (manual entries) section. Defaults to
   * true. Set false for rows that have no manual-entry capability (credit,
   * employment, personal identity) so they don't show a dead "No sources
   * yet" empty state.
   */
  showSources?: boolean;

  // ── Verification CTAs ──────────────────────────────────────────────────────
  /** Called when "Verify with bank" is clicked. Optional; hides the button if absent. */
  onConnectBank?: () => void;
  /** Called when "Upload document" is clicked. Optional; hides the button if absent. */
  onUploadDocs?: () => void;
  /**
   * When true, "Verify with bank" renders as a non-interactive "Coming soon"
   * chip instead of a button. Used while Plaid Bank Income is ramping.
   */
  bankComingSoon?: boolean;
  /**
   * Server-authored explanation (task 1678) for why this field cannot be
   * evidenced by a document — rendered verbatim in place of the "Upload
   * document" button. Set only when the capability is false; the caller
   * reads it from GET /api/verification/field-capabilities.
   *
   * The button and the explanation are separate props rather than one
   * union because they are separate FACTS: the handler's absence is what
   * hides the button, and the reason is what fills the space it left. A
   * missing button with no explanation reads as breakage, which is why the
   * "How you earn" row shipped an upload affordance in the first place —
   * nobody wanted a blank spot there.
   */
  documentUnsupportedReason?: string;

  // ── Summary inset ──────────────────────────────────────────────────────────
  /** Eyebrow label for the summary inset, e.g. "TOTAL INCOME". */
  summaryEyebrow?: string;
  /** Aggregate value string, e.g. "$125,000/yr". */
  summaryValue?: string;

  // ── Sources list (manual entries) ─────────────────────────────────────────
  entries?: ManualFinancialEntry[];
  entriesLoading?: boolean;
  addLabel?: string;
  /**
   * entryKind: the ManualEntryKind for the inline add form. Required when
   * onCreateSource is provided; controls which fields the form renders.
   */
  entryKind?: ManualEntryKind;
  /**
   * onCreateSource: called when the user submits the inline add-source form
   * inside the drawer. When provided, "+ Add source" opens the inline form
   * rather than routing the user away. When absent, the pill is hidden.
   *
   * Replaces the old onAddSource callback (which closed the drawer — a bug).
   */
  onCreateSource?: (req: ManualEntryRequest) => Promise<void>;
  onDeleteSource?: (id: string) => Promise<void>;
  /**
   * deletingId: the entry.id currently being deleted (shows spinner on that
   * row's trash button, blocks double-clicks).
   */
  deletingId?: string | null;
}

// ─── Drawer ──────────────────────────────────────────────────────────────────

export function EditAndVerifyDrawer(props: EditAndVerifyDrawerProps) {
  const verificationAction = useRef<(() => void) | undefined>(undefined);
  const handOffVerification = (action: () => void) => {
    verificationAction.current = action;
    props.onClose();
  };
  return (
    <DialogPrimitive.Root
      open={props.open}
      onOpenChange={(open) => {
        if (!open) props.onClose();
      }}
    >
      <AnimatePresence
        onExitComplete={() => {
          const action = verificationAction.current;
          verificationAction.current = undefined;
          // Let the modal release focus and pointer/scroll locks before Plaid
          // or the document dialog takes over outside this portal.
          if (action) requestAnimationFrame(action);
        }}
      >
        {props.open && (
          <EditAndVerifyDrawerContent
            key={props.rowId}
            {...props}
            onConnectBank={
              props.onConnectBank
                ? () => handOffVerification(props.onConnectBank!)
                : undefined
            }
            onUploadDocs={
              props.onUploadDocs
                ? () => handOffVerification(props.onUploadDocs!)
                : undefined
            }
          />
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}

// Presence retains the outgoing field and editor until the slide finishes.
// Unmounting also resets the inline form for the next field/open.
function EditAndVerifyDrawerContent({
  onClose,
  fieldLabel,
  valueEditor,
  showSources = true,
  onConnectBank,
  onUploadDocs,
  bankComingSoon = false,
  documentUnsupportedReason,
  summaryEyebrow,
  summaryValue,
  entries = [],
  entriesLoading = false,
  addLabel = "Add source",
  entryKind,
  onCreateSource,
  onDeleteSource,
  deletingId,
}: EditAndVerifyDrawerProps) {
  const reduceMotion = useReducedMotion();
  const panelRef = useRef<HTMLDivElement>(null);
  const [returnFocusTo] = useState(() => document.activeElement);

  // Inline add-source form state — controlled here so the drawer stays open.
  const [isAddingSource, setIsAddingSource] = useState(false);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const { x, ...swipeHandlers } = useMobileDrawerSwipe(
    onClose,
    isAddingSource || addSubmitting || Boolean(deletingId),
  );
  const sourceCount = entries.length;

  const handleAddSourceSubmit = async (req: ManualEntryRequest) => {
    if (!onCreateSource) return;
    setAddSubmitting(true);
    setAddError(null);
    try {
      await onCreateSource(req);
      setIsAddingSource(false);
    } catch {
      setAddError("Failed to save. Please try again.");
    } finally {
      setAddSubmitting(false);
    }
  };

  // One name for "is there anything the buyer can click here". Used three
  // times below: to render the section at all, to render the button row, and
  // to render the blurb that promises those buttons exist.
  const hasVerifyAction = Boolean(
    onConnectBank || onUploadDocs || bankComingSoon,
  );

  return (
    <DialogPrimitive.Portal forceMount>
      <div className="pointer-events-none fixed inset-0 z-50 overflow-hidden">
        <DialogPrimitive.Overlay forceMount asChild>
          <motion.div
            className="pointer-events-auto absolute inset-0 bg-foreground/20 backdrop-blur-[1px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.2 }}
            onClick={onClose}
          />
        </DialogPrimitive.Overlay>
        <DialogPrimitive.Content
          forceMount
          asChild
          aria-describedby={undefined}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (
              returnFocusTo instanceof HTMLElement &&
              returnFocusTo.isConnected
            )
              returnFocusTo.focus();
          }}
          onEscapeKeyDown={(event) => {
            if (isAddingSource) {
              event.preventDefault();
              setIsAddingSource(false);
              setAddError(null);
            }
          }}
        >
          <motion.div
            {...swipeHandlers}
            ref={panelRef}
            style={{ x, touchAction: "pan-y pinch-zoom" }}
            aria-label={`Edit and verify ${fieldLabel}`}
            data-testid="edit-verify-drawer"
            initial={{ x: reduceMotion ? 0 : "100%" }}
            animate={{ x: 0 }}
            variants={{
              closed: () => ({
                // Keep exit in pixels after a drag, avoiding a px-to-% jump.
                x: reduceMotion
                  ? 0
                  : (panelRef.current?.getBoundingClientRect().width ??
                    window.innerWidth),
              }),
            }}
            exit="closed"
            transition={{
              duration: reduceMotion ? 0 : 0.28,
              ease: [0.2, 0, 0.13, 1],
            }}
            className={cn(
              "pointer-events-auto absolute inset-y-0 right-0 flex w-full flex-col bg-popover shadow-xl outline-none",
              "sm:max-w-[22rem] md:max-w-[24rem]",
            )}
          >
            {/* ── Header ────────────────────────────────────────────────────── */}
            <div className="flex-shrink-0 px-5 pt-5 pb-4 border-b border-border">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[0.6rem] font-semibold uppercase leading-none tracking-[0.22em] text-muted-foreground">
                    Edit &amp; Verify
                  </p>
                  <DialogPrimitive.Title asChild>
                    <h2 className="mt-1.5 text-xl font-bold leading-tight tracking-[-0.02em] text-foreground">
                      {fieldLabel}
                    </h2>
                  </DialogPrimitive.Title>
                </div>
                {/* Circular X button */}
                <button
                  type="button"
                  aria-label="Close drawer"
                  onClick={onClose}
                  data-testid="drawer-close"
                  className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-border bg-card text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <X aria-hidden="true" className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* ── Scrollable body ────────────────────────────────────────────── */}
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5 space-y-5">
              {/* Scalar value editor (credit band / work history) */}
              {valueEditor}

              {/* Verification action buttons */}
              {(hasVerifyAction || documentUnsupportedReason) && (
                <div className="space-y-3">
                  {hasVerifyAction && (
                    <div className="flex flex-wrap gap-2.5">
                      {bankComingSoon ? (
                        <span
                          className="inline-flex items-center gap-2 rounded-2xl border border-border bg-muted px-4 py-2 text-sm text-muted-foreground cursor-not-allowed opacity-60"
                          title="Coming soon"
                        >
                          <Landmark aria-hidden="true" className="h-4 w-4" />
                          Verify with bank
                        </span>
                      ) : onConnectBank ? (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={onConnectBank}
                          data-testid="drawer-connect-bank"
                          className="gap-2"
                        >
                          <Landmark aria-hidden="true" className="h-4 w-4" />
                          Verify with bank
                        </Button>
                      ) : null}
                      {onUploadDocs && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={onUploadDocs}
                          data-testid="drawer-upload-doc"
                          className="gap-2"
                        >
                          <Upload aria-hidden="true" className="h-4 w-4" />
                          Upload document
                        </Button>
                      )}
                    </div>
                  )}
                  {documentUnsupportedReason && (
                    <p
                      data-testid="drawer-document-unsupported"
                      className="text-xs leading-5 text-muted-foreground"
                    >
                      {documentUnsupportedReason}
                    </p>
                  )}
                  {/* The generic blurb promises "connect an account or add a
                  file" — only true when at least one of those buttons is
                  actually on screen. */}
                  {hasVerifyAction && (
                    <p className="text-xs leading-5 text-muted-foreground">
                      One click launches the full secure journey — connect an
                      account or add a file. Anything we can&apos;t pull
                      automatically, add by hand below.
                    </p>
                  )}
                </div>
              )}

              {/* Summary inset */}
              {(summaryEyebrow ?? summaryValue) && (
                <div className="rounded-xl bg-muted/60 px-4 py-3 space-y-0.5">
                  {summaryEyebrow && (
                    <p className="text-[0.6rem] font-semibold uppercase leading-none tracking-[0.22em] text-muted-foreground">
                      {summaryEyebrow}
                    </p>
                  )}
                  {summaryValue && (
                    <p className="text-lg font-bold tabular-nums text-foreground mt-1">
                      {summaryValue}
                    </p>
                  )}
                  {sourceCount > 0 && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {sourceCount} {sourceCount === 1 ? "source" : "sources"}
                    </p>
                  )}
                </div>
              )}

              {/* Sources section */}
              {showSources && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-[0.6rem] font-semibold uppercase leading-none tracking-[0.22em] text-muted-foreground">
                      Sources
                    </p>
                    {/* "+ Add source" pill — only shown when an inline form can be opened */}
                    {onCreateSource && !isAddingSource && (
                      <button
                        type="button"
                        onClick={() => setIsAddingSource(true)}
                        data-testid="drawer-add-source"
                        className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:bg-accent hover:border-primary/30"
                      >
                        <Plus aria-hidden="true" className="h-3 w-3" />
                        {addLabel}
                      </button>
                    )}
                  </div>

                  {/* Inline add form — appears INSIDE the drawer, drawer stays open */}
                  {isAddingSource && entryKind && (
                    <div data-testid="drawer-add-source-form">
                      <ManualEntryForm
                        kind={entryKind}
                        isSubmitting={addSubmitting}
                        error={addError}
                        onSubmit={(req) => void handleAddSourceSubmit(req)}
                        onCancel={() => {
                          setIsAddingSource(false);
                          setAddError(null);
                        }}
                      />
                    </div>
                  )}

                  {entriesLoading ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading…
                    </div>
                  ) : entries.length === 0 && !isAddingSource ? (
                    <p className="text-sm text-muted-foreground">
                      No sources yet. Add one above.
                    </p>
                  ) : (
                    <ul className="divide-y divide-border-soft">
                      {entries.map((entry) => {
                        const tier = entryTierFromSource(entry);
                        const value = formatEntryValue(entry);
                        const isDeleting = deletingId === entry.id;
                        const isEditable =
                          entry.editable ??
                          (entry.source_family ?? entry.source) === "manual";

                        return (
                          <li
                            key={entry.id}
                            className="flex items-center justify-between gap-3 py-3"
                          >
                            <div className="min-w-0 flex-1 space-y-1">
                              <p className="truncate text-sm font-medium text-foreground">
                                {entry.display_name}
                              </p>
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-xs text-muted-foreground">
                                  {value}
                                </span>
                                <TierBadge tier={tier} size="sm" />
                              </div>
                            </div>
                            {isEditable && onDeleteSource && (
                              <button
                                type="button"
                                aria-label={`Delete ${entry.display_name}`}
                                disabled={isDeleting}
                                onClick={() => void onDeleteSource(entry.id)}
                                className="flex-shrink-0 flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:pointer-events-none disabled:opacity-50"
                              >
                                {isDeleting ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2
                                    aria-hidden="true"
                                    className="h-4 w-4"
                                  />
                                )}
                              </button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </div>

            {/* ── Sticky footer ─────────────────────────────────────────────── */}
            <div className="flex-shrink-0 border-t border-border px-5 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
              <Button
                variant="default"
                size="default"
                onClick={onClose}
                data-testid="drawer-done"
                className="w-full"
              >
                Done
              </Button>
            </div>
          </motion.div>
        </DialogPrimitive.Content>
      </div>
    </DialogPrimitive.Portal>
  );
}

// ─── Row icon glyph (duplicated from AggregateInputRow to avoid coupling) ────

export function DrawerRowGlyph({ rowId }: { rowId: ProfileRowId }) {
  const props = {
    "aria-hidden": true,
    className: "size-5",
    strokeWidth: 1.75,
  } as const;

  switch (rowId) {
    case "income":
      return <Banknote {...props} />;
    case "employment":
      return <BriefcaseBusiness {...props} />;
    case "cash_assets":
      return <Landmark {...props} />;
    case "debts":
      return <ShieldCheck {...props} />;
    case "income_shape":
      return <Wallet {...props} />;
    case "personal_identity":
      return <UserRoundCheck {...props} />;
    default:
      return <ShieldCheck {...props} />;
  }
}

// ─── Helper: manual entry delete handler factory ─────────────────────────────
// Used by callers who need to wire a delete with loading state without re-
// implementing the spinner/try-catch pattern.

// ─── Wire helper for ManualEntryRequest ──────────────────────────────────────
// Re-export the type so callers can import from one place.
export type { ManualEntryRequest };
